"use strict";
/*!
 * Buttplug JS Source Code File - Visit https://buttplug.io for more info about
 * the project. Licensed under the BSD 3-Clause license. See LICENSE file in the
 * project root for full license information.
 *
 * @copyright Copyright (c) Nonpolynomial Labs LLC. All rights reserved.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebBluetoothDevice = void 0;
const Exceptions_1 = require("../../../core/Exceptions");
const ButtplugDeviceImpl_1 = require("../../../devices/ButtplugDeviceImpl");
const Endpoints_1 = require("../../../devices/Endpoints");
const ButtplugDeviceReadOptions_1 = require("../../../devices/ButtplugDeviceReadOptions");
class WebBluetoothDevice extends ButtplugDeviceImpl_1.ButtplugDeviceImpl {
    constructor(_deviceConfig, _device) {
        super(_device.name, _device.id);
        this._deviceConfig = _deviceConfig;
        this._device = _device;
        this._notificationHandlers = new Map();
        this._characteristics = new Map();
        this.Connect = () => __awaiter(this, void 0, void 0, function* () {
            this._logger.Debug(`WebBluetoothDevice: ${this.constructor.name} connecting`);
            this._device.addEventListener("gattserverdisconnected", this.OnDisconnect);
            this._server = yield this._device.gatt.connect();
            for (const [configService, configChrs] of this._deviceConfig.Services) {
                // We passed along a list of services we expect to work with all hardware
                // as part of the connection filters, so these services will be found when
                // running getPrimaryService
                let service;
                try {
                    // If we don't include this block, tests fail because the WebBluetooth
                    // mock we use doesn't implement getPrimaryService checking, it'll
                    // always return a service. Therefore we have to add non-standard
                    // functions to our tests. This is so dumb. :(
                    try {
                        if (!this._device.hasService(configService)) {
                            continue;
                        }
                    }
                    catch (e) {
                        // no-op, just continue. This block will only succeed during testing.
                        // However, it's also not in any hot path, so we're fine with the
                        // constant triggering of try/catch in production.
                    }
                    service = yield this._server.getPrimaryService(configService);
                }
                catch (e) {
                    // We may run into services that aren't actually on the current device.
                    // For instance, we have a long list of expected services for Lovense
                    // devices. In this case, log and continue looking through the list.
                    this._logger.Debug(`Cannot find service ${configService} on ${this._device.name}, continuing search: ${e}`);
                    continue;
                }
                // If no characteristics are present in the DeviceInfo block, we assume that
                // we're connecting to a simple rx/tx service, and can query to figure out
                // characteristics. Assume that the characteristics have tx/rx references.
                //
                // Note: This will always fail for WebBLE/iOS WebBluetooth at the moment,
                // as the polyfill lacks any enumeration functionality. Only use this as a
                // last resort.
                if (configChrs.size === 0) {
                    let characteristics = [];
                    try {
                        characteristics = yield service.getCharacteristics();
                    }
                    catch (e) {
                        // If we have a device that requires characteristic searches, but
                        // whatever polyfill we're using doesn't allow for it, just complain
                        // and refuse to connect.
                        const errMsg = `getCharacteristics not implemented on this platform, cannot connect to ${this._device.name}`;
                        throw Exceptions_1.ButtplugException.LogAndError(Exceptions_1.ButtplugDeviceException, this._logger, errMsg);
                    }
                    for (const char of characteristics) {
                        if (char.properties.write ||
                            char.properties.writeWithoutResponse ||
                            char.properties.reliableWrite) {
                            this.SetNewCharacteristic(Endpoints_1.Endpoints.Tx, char);
                        }
                        else if (char.properties.read ||
                            char.properties.broadcast ||
                            char.properties.notify ||
                            char.properties.indicate) {
                            this.SetNewCharacteristic(Endpoints_1.Endpoints.Rx, char);
                        }
                    }
                    continue;
                }
                for (const [chrEndpoint, chrUuid] of configChrs) {
                    // Assume that our characteristic name is correct if we've gotten this
                    // far. Hopefully not a wrong assumption at the moment, though it's
                    // something we'll need to change once arbitrary string endpoints are
                    // allowed.
                    this.SetNewCharacteristic(Endpoints_1.GetEndpoint(chrEndpoint), yield service.getCharacteristic(chrUuid));
                }
            }
            // If at this point we still don't have any characteristics, something is
            // wrong, error out.
            if (this._characteristics.size === 0) {
                throw new Exceptions_1.ButtplugDeviceException(`No usable characteristics found for ${this._device.name}`);
            }
        });
        this.Disconnect = () => __awaiter(this, void 0, void 0, function* () {
            for (const chr of this._notificationHandlers.keys()) {
                yield this.Unsubscribe(new ButtplugDeviceReadOptions_1.ButtplugDeviceReadOptions({ Endpoint: chr }));
            }
            this._server.disconnect();
        });
        this.OnDisconnect = () => {
            this._logger.Debug(`WebBluetoothDevice: ${this.constructor.name} disconnecting`);
            this._device.removeEventListener("gattserverdisconnected", this.OnDisconnect);
            this.emit("deviceremoved");
        };
        this.WriteValueInternal = (aValue, aOptions) => __awaiter(this, void 0, void 0, function* () {
            this.CheckForCharacteristic(aOptions.Endpoint);
            const chr = this._characteristics.get(aOptions.Endpoint);
            // this._logger.Trace(`WebBluetoothDevice: ${this.constructor.name} writing ${aValue} to ${chr.uuid}`);
            return yield chr.writeValue(aValue);
        });
        this.ReadValueInternal = (aOptions) => __awaiter(this, void 0, void 0, function* () {
            this.CheckForCharacteristic(aOptions.Endpoint);
            const chr = this._characteristics.get(aOptions.Endpoint);
            // this._logger.Trace(`WebBluetoothDevice: ${this.constructor.name} reading from ${chr.uuid}`);
            return Buffer.from((yield chr.readValue()).buffer);
        });
        this.SubscribeToUpdatesInternal = (aOptions) => __awaiter(this, void 0, void 0, function* () {
            this.CheckForCharacteristic(aOptions.Endpoint);
            if (this._notificationHandlers.has(aOptions.Endpoint)) {
                const err = `${this._device.name} already subscribed to updates on characteristic ${aOptions.Endpoint}`;
                throw Exceptions_1.ButtplugException.LogAndError(Exceptions_1.ButtplugDeviceException, this._logger, err);
            }
            const chr = this._characteristics.get(aOptions.Endpoint);
            this._logger.Trace(`WebBluetoothDevice: ${this.constructor.name} subscribing to updates from ${chr.uuid}`);
            yield chr.startNotifications();
            this._notificationHandlers.set(aOptions.Endpoint, (event) => {
                this.CharacteristicValueChanged(event, aOptions.Endpoint);
            });
            chr.addEventListener("characteristicvaluechanged", this._notificationHandlers.get(aOptions.Endpoint));
        });
        this.Unsubscribe = (aOptions) => __awaiter(this, void 0, void 0, function* () {
            this.CheckForCharacteristic(aOptions.Endpoint);
            if (!this._notificationHandlers.has(aOptions.Endpoint)) {
                const err = `${this._device.name} not subscribed to updates on characteristic ${aOptions.Endpoint}`;
                throw Exceptions_1.ButtplugException.LogAndError(Exceptions_1.ButtplugDeviceException, this._logger, err);
            }
            const chr = this._characteristics.get(aOptions.Endpoint);
            this._logger.Trace(`WebBluetoothDevice: ${this.constructor.name} unsubscribing to updates from ${chr.uuid}`);
            chr.removeEventListener("characteristicvaluechanged", this._notificationHandlers.get(aOptions.Endpoint));
            this._notificationHandlers.delete(aOptions.Endpoint);
            yield chr.stopNotifications();
        });
        this.CharacteristicValueChanged = (aEvent, aCharacteristic) => {
            const view = aEvent.target.value;
            const arrBuf = view.buffer;
            const buf = Buffer.from(arrBuf, view.byteOffset, view.byteLength);
            this.UpdateReceived(aCharacteristic, buf);
        };
    }
    get Connected() {
        return this._server.connected;
    }
    CheckForCharacteristic(aEndpoint) {
        if (this._characteristics.has(aEndpoint)) {
            return;
        }
        const err = `Characteristic ${aEndpoint} does not exist on device ${this._device.name}.`;
        throw Exceptions_1.ButtplugException.LogAndError(Exceptions_1.ButtplugDeviceException, this._logger, err);
    }
    SetNewCharacteristic(aEndpoint, aChr) {
        if (this._characteristics.has(aEndpoint)) {
            throw new Exceptions_1.ButtplugDeviceException(`Endpoint ${aEndpoint} already defined on ${this._device.name}`);
        }
        this._characteristics.set(aEndpoint, aChr);
    }
}
exports.WebBluetoothDevice = WebBluetoothDevice;
//# sourceMappingURL=WebBluetoothDevice.js.map